import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import * as microsoftTeams from '@microsoft/teams-js'
import {
  setUserInfo as setApiUserInfo,
  setAzureTenantId,
  setSsoToken,
  clearSsoToken,
} from '../lib/api'

interface TeamsContextType {
  isInitialized: boolean
  isInTeams: boolean
  hasSsoToken: boolean
  theme: 'light' | 'dark' | 'contrast'
  userInfo: microsoftTeams.app.Context | null
  error: string | null
}

const TeamsContext = createContext<TeamsContextType>({
  isInitialized: false,
  isInTeams: false,
  hasSsoToken: false,
  theme: 'light',
  userInfo: null,
  error: null,
})

export function useTeams() {
  return useContext(TeamsContext)
}

interface TeamsProviderProps {
  children: ReactNode
}

function formatTeamsAuthError(error: unknown): string {
  if (!error) return 'unknown_error'
  if (typeof error === 'string') return error

  const maybeObj = error as Record<string, unknown>
  const code = typeof maybeObj.errorCode === 'string' ? maybeObj.errorCode : ''
  const message =
    typeof maybeObj.message === 'string'
      ? maybeObj.message
      : typeof maybeObj.error === 'string'
        ? maybeObj.error
        : ''

  return [code, message].filter(Boolean).join(': ') || JSON.stringify(maybeObj)
}

export function TeamsProvider({ children }: TeamsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isInTeams, setIsInTeams] = useState(false)
  const [hasSsoToken, setHasSsoToken] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark' | 'contrast'>('light')
  const [userInfo, setUserInfo] = useState<microsoftTeams.app.Context | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        await microsoftTeams.app.initialize()
        setIsInTeams(true)

        // Get Teams context
        const context = await microsoftTeams.app.getContext()
        setUserInfo(context)

        if (context.user) {
          setApiUserInfo(
            context.user.userPrincipalName || '',
            context.user.displayName || 'Teams User',
            context.user.id || ''
          )

          if (context.user.tenant?.id) {
            setAzureTenantId(context.user.tenant.id)
          }
        }

        // Acquire Teams SSO token for server-side verification.
        // First try silent; if consent/interaction is required, try interactive.
        try {
          let token = ''

          try {
            token = await microsoftTeams.authentication.getAuthToken({ silent: true })
          } catch (silentErr) {
            console.warn('Teams silent SSO token acquisition failed:', formatTeamsAuthError(silentErr))
            token = await microsoftTeams.authentication.getAuthToken({ silent: false })
          }

          if (!token) {
            throw new Error('No token was returned from Teams SSO.')
          }

          setSsoToken(token)
          setHasSsoToken(true)
          setError(null)
        } catch (tokenErr) {
          const details = formatTeamsAuthError(tokenErr)
          console.warn('Teams SSO token acquisition failed:', details)
          clearSsoToken()
          setHasSsoToken(false)
          setError(`Could not establish Teams SSO session: ${details}`)
        }

        // Set theme based on Teams context
        if (context.app.theme === 'dark') {
          setTheme('dark')
        } else if (context.app.theme === 'contrast') {
          setTheme('contrast')
        } else {
          setTheme('light')
        }

        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          if (newTheme === 'dark') {
            setTheme('dark')
          } else if (newTheme === 'contrast') {
            setTheme('contrast')
          } else {
            setTheme('light')
          }
        })

        microsoftTeams.app.notifySuccess()
        setIsInitialized(true)
      } catch (err) {
        // Initialization can fail when opened outside Teams or if the host cannot establish SDK messaging.
        const details = formatTeamsAuthError(err)
        console.warn(`Teams context initialization failed: ${details}`)
        setIsInTeams(false)
        setIsInitialized(true)
        setError(`Teams host context was not detected: ${details}`)

        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          setTheme('dark')
        }
      }
    }

    initializeTeams()
  }, [])

  return (
    <TeamsContext.Provider value={{ isInitialized, isInTeams, hasSsoToken, theme, userInfo, error }}>
      {children}
    </TeamsContext.Provider>
  )
}
